import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { setScrollLock } from './scrollLock';
import { canonicalTech } from './data/projects';

/**
 * Shared interaction state.
 *
 * Three things need to reach across component boundaries: the command palette
 * opens a project that the projects section renders, a technology chip in the
 * skills section filters the projects grid, and both need to scroll the page.
 * Keeping that here avoids threading callbacks through every level.
 *
 * The filter state also round-trips through the URL, so a filtered view is a
 * link someone can send.
 */

const SiteContext = createContext(null);

export const SORTS = [
  { key: 'recent', labelKey: 'sort.recent' },
  { key: 'oldest', labelKey: 'sort.oldest' },
  { key: 'az', labelKey: 'sort.az' },
];

const DEFAULTS = { query: '', category: 'all', techs: [], sort: 'recent' };

export const useSite = () => {
  const value = useContext(SiteContext);
  if (!value) throw new Error('useSite must be used inside <SiteProvider>');
  return value;
};

export function scrollToSection(sectionId) {
  const element = document.getElementById(sectionId);
  if (!element) return;

  // Number.isFinite, not `|| 68`: Console legitimately sets --header-h: 0px,
  // and 0 is falsy — `0 || 68` would silently fall back to 68 and land every
  // scroll-to-section 76px too low in that mode.
  const parsedHeaderHeight = parseInt(
    getComputedStyle(document.documentElement).getPropertyValue('--header-h'),
    10
  );
  const headerHeight = Number.isFinite(parsedHeaderHeight) ? parsedHeaderHeight : 68;

  // Measured against the viewport rather than offsetTop: the latter is relative
  // to the nearest positioned ancestor, which is wrong for nested targets.
  const top = element.getBoundingClientRect().top + window.scrollY - headerHeight - 8;

  window.scrollTo({ top, behavior: 'smooth' });
}

const scrollToGrid = () => {
  requestAnimationFrame(() =>
    scrollToSection(document.getElementById('projects-filter') ? 'projects-filter' : 'projects')
  );
};

function readUrlState() {
  if (typeof window === 'undefined') return DEFAULTS;
  const params = new URLSearchParams(window.location.search);
  const techs = params.get('tech');
  return {
    query: params.get('q') || DEFAULTS.query,
    category: params.get('category') || DEFAULTS.category,
    techs: techs ? techs.split(',').filter(Boolean) : DEFAULTS.techs,
    sort: SORTS.some((s) => s.key === params.get('sort')) ? params.get('sort') : DEFAULTS.sort,
  };
}

export function SiteProvider({ children }) {
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectTab, setProjectTab] = useState('shots');

  const initial = useMemo(readUrlState, []);
  const [query, setQuery] = useState(initial.query);
  const [categoryFilter, setCategoryFilter] = useState(initial.category);
  const [techFilters, setTechFilters] = useState(initial.techs);
  const [sort, setSort] = useState(initial.sort);

  const hasFilters =
    query.trim() !== '' || categoryFilter !== 'all' || techFilters.length > 0;

  // Mirror the filters into the address bar so a filtered view can be shared.
  // replaceState, not pushState: filtering is not navigation, and stacking
  // history entries would make the back button feel broken.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const apply = (key, value, fallback) => {
      if (value && value !== fallback) params.set(key, value);
      else params.delete(key);
    };

    apply('q', query.trim(), '');
    apply('category', categoryFilter, 'all');
    apply('tech', techFilters.join(','), '');
    apply('sort', sort, 'recent');

    const search = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`
    );
  }, [query, categoryFilter, techFilters, sort]);

  // The second argument lets the command palette open a project straight onto
  // its diagram; everything else opens on the screenshots.
  const openProject = useCallback((project, tab = 'shots') => {
    setSelectedProject(project);
    setProjectTab(tab);
    setScrollLock('modal', true);
  }, []);

  const closeProject = useCallback(() => {
    setSelectedProject(null);
    setScrollLock('modal', false);
  }, []);

  // Used from outside the projects section (skills chips, command palette):
  // replace the stack selection and take the reader to the result.
  const filterByTech = useCallback(
    (tech) => {
      setTechFilters([canonicalTech(tech)]);
      closeProject();
      scrollToGrid();
    },
    [closeProject]
  );

  // Used from inside the grid: add or remove one technology, stay put.
  const toggleTech = useCallback((tech) => {
    const name = canonicalTech(tech);
    setTechFilters((current) =>
      current.includes(name) ? current.filter((t) => t !== name) : [...current, name]
    );
  }, []);

  const clearFilters = useCallback(() => {
    setQuery(DEFAULTS.query);
    setCategoryFilter(DEFAULTS.category);
    setTechFilters(DEFAULTS.techs);
  }, []);

  const value = useMemo(
    () => ({
      selectedProject,
      projectTab,
      setProjectTab,
      openProject,
      closeProject,
      query,
      setQuery,
      categoryFilter,
      setCategoryFilter,
      techFilters,
      filterByTech,
      toggleTech,
      sort,
      setSort,
      hasFilters,
      clearFilters,
    }),
    [
      selectedProject,
      projectTab,
      setProjectTab,
      openProject,
      closeProject,
      query,
      categoryFilter,
      techFilters,
      filterByTech,
      toggleTech,
      sort,
      hasFilters,
      clearFilters,
    ]
  );

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}
